<template>
	<div>
		<Layout>
			<div class="install-container" ref="wizard" :disabled="lockControls ? true : null">
				<div class="install-card" tabscope tabindex="0">
					<!-- Desktop Sidebar (Right side in RTL) -->
					<div class="install-sidebar">
						<div class="brand-header">
							<img class="brand-logo" :src="logoWhite" alt="Nikan" />
							<div class="brand-title">
								<span>{{ $t('install.brand-title') }} </span>
								<span class="brand-name-bold">{{ $t('install.brand-name') }}</span>
							</div>
						</div>
						<div class="stepper-list">
							<div v-for="(step, index) in stepList" :key="step.id" class="stepper-item" :class="{
								active: currentStepIndex === index,
								completed: currentStepIndex > index
							}">
								<div class="stepper-badge-wrapper">
									<div class="stepper-badge">
										<icon :icon="currentStepIndex > index ? 'check' : step.icon" group="regular" />
									</div>
									<div class="stepper-line" v-if="index < stepList.length - 1"></div>
								</div>
								<div class="stepper-info">
									<div class="stepper-title">{{ $t(`install.wizard.sidebarTitle.${step.id}`) }}</div>
									<div class="stepper-subtitle">{{ $t(`install.wizard.sidebarSubtitle.${step.id}`) }}</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Mobile Header Banner (Curved Top in Mobile) -->
					<div class="install-mobile-header">
						<div class="mobile-stepper-row">
							<!-- Completed steps before current step (Right side in RTL) -->
							<transition-group :name="direction === 'forward' ? 'mobile-completed-forward' : 'mobile-completed-backward'" tag="div" class="mobile-completed-group">
								<div v-for="(step, index) in completedSteps" :key="'completed-' + step.id" class="mobile-step-badge completed">
									<icon icon="check" group="regular" />
								</div>
							</transition-group>

							<!-- Active step with animated transition -->
							<div class="mobile-active-wrapper">
								<transition :name="direction === 'forward' ? 'mobile-active-forward' : 'mobile-active-backward'" mode="out-in">
									<div :key="'active-' + currentStepId" class="mobile-step-badge active">
										<icon :icon="stepList[currentStepIndex]?.icon" group="regular" />
									</div>
								</transition>
							</div>

							<!-- Connector line between active and future steps with fade transition -->
							<transition name="mobile-connector-fade">
								<div class="mobile-step-connector" v-if="hasRemainingSteps"></div>
							</transition>

							<!-- Future steps after current step (Left side in RTL) -->
							<transition-group :name="direction === 'forward' ? 'mobile-future-forward' : 'mobile-future-backward'" tag="div" class="mobile-other-steps">
								<div v-for="(step, index) in futureSteps" :key="'future-' + step.id" class="mobile-step-badge">
									<icon :icon="step.icon" group="regular" />
								</div>
							</transition-group>
						</div>
						<transition name="header-fade" mode="out-in">
							<div :key="currentStepId" class="mobile-header-text">
								<h2 class="mobile-step-heading">{{ $t(`install.wizard.title.${currentStepId}`) }}</h2>
								<p class="mobile-step-subheading">{{ $t(`install.wizard.subtitle.${currentStepId}`) }}</p>
							</div>
						</transition>
					</div>

					<!-- Main Form Section -->
					<div class="install-form-section">
						<div class="form-header-desktop">
							<transition name="header-fade" mode="out-in">
								<div :key="currentStepId" class="header-text-wrapper">
									<div class="step-counter">{{ currentStepIndex + 1 }}/{{ stepList.length }}</div>
									<h1 class="step-heading">{{ $t(`install.wizard.title.${currentStepId}`) }}</h1>
									<p class="step-subheading">{{ $t(`install.wizard.subtitle.${currentStepId}`) }}</p>
								</div>
							</transition>
							<div class="header-separator"></div>
						</div>

						<div class="form-content-body" ref="component-container">
							<transition
								:name="direction === 'forward' ? 'step-fade-forward' : 'step-fade-backward'"
								mode="out-in"
								@after-enter="onStepEntered"
							>
								<component :is="currentStep" :key="currentStepId" :modelValue="environment" @update:modelValue="environmentChanged" />
							</transition>
						</div>

						<div class="form-action-buttons">
							<button type="button" class="btn-action-prev" tabindex="99" :disabled="isSending ? true : null" @click="onPrevClicked">
								<icon icon="chevron-right" class="action-icon" group="regular" />
								<span>{{ $t('install.buttons.prev') }}</span>
							</button>

							<button v-if="!isLastStep" type="button" class="btn-action-next" id="install-next-step" tabindex="98" @click="onNextClicked">
								<span>{{ $t('install.buttons.next') }}</span>
								<icon icon="chevron-left" class="action-icon" group="regular" />
							</button>
							<button v-else type="button" class="btn-action-next btn-action-install" id="install-next-step" tabindex="98" :disabled="isSending ? true : null" @click="onInstallClicked">
								<span>{{ $t('install.buttons.install') }}</span>
								<icon icon="cog" class="action-icon" :class="{ 'icon-spin': isSending }" group="regular" />
							</button>
						</div>
					</div>

					<!-- Custom Installing Loader Overlay -->
					<transition name="install-loading-fade">
						<div v-if="isSending" class="install-loading-overlay">
							<div class="install-loading-card">
								<div class="install-custom-loader"></div>
							</div>
						</div>
					</transition>
				</div>
			</div>
		</Layout>
		<nk-dialog :messages="dialogMessages"></nk-dialog>
	</div>
</template>

<script>
	import _ from 'lodash'
	import $ from 'jquery'
	import Layout from '~/views/shared/_layout-login'
	import logoWhite from '@/assets/logo-white.png'
	import PreparingStep from './preparing'
	import AdminDetailsStep from './admin-details'
	import CodingsStep from './codings'
	import CompanyFiscalStep from './company-fiscal'
	import Review from './review'
	import $sc from '~/plugins/shortcut.collection.js'
	import TabOrder from '~/statics/taborder-functions'
	import validators, { Checker, VEqual, VLength, isEmpty } from '~/statics/validators'
	import { post } from '~/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import { mapActions, mapGetters, mapState } from 'vuex'
	import { markRaw } from 'vue'
	import router from '~/plugins/router'

	const markRawComponent = (component) => markRaw(component)

	const _PreparingStep = markRawComponent(PreparingStep)
	const _AdminDetailsStep = markRawComponent(AdminDetailsStep)
	const _CodingsStep = markRawComponent(CodingsStep)
	const _CompanyFiscalStep = markRawComponent(CompanyFiscalStep)
	const _Review = markRawComponent(Review)

	export default {
		components: {
			Layout,
		},
		data() {
			return {
				direction: 'forward',
				logoWhite,
				message: null,
				environment: {
					adminDetails: {
						username: null,
						password: null,
						confirmPassword: null,
						email: null,
					},
					codings: {
						install: true,
					},
					useDefaultFloatingCenterGroup: true,
					company: {
						name: null,
					},
					fiscalPeriod: {
						name: null,
						startDate: null,
						endDate: null,
					},
				},
				stepList: [
					{
						id: 'admin-details',
						icon: 'user',
						component: _AdminDetailsStep,
					},
					{
						id: 'codings',
						icon: 'sitemap',
						component: _CodingsStep,
					},
					{
						id: 'company-fiscal',
						icon: 'calendar-alt',
						component: _CompanyFiscalStep,
					},
					{
						id: 'review',
						icon: 'check-double',
						component: _Review,
					},
				],
				steps: [_AdminDetailsStep, _CodingsStep, _CompanyFiscalStep, _Review],
				currentStep: _AdminDetailsStep,

				nextButtonOptions: {
					title: this.$t('install.buttons.next'),
					shortcut: $sc.find('next', 'install'),
					isPrimary: true,
					handle: this.onNextClicked,
				},
				prevButtonOptions: {
					title: this.$t('install.buttons.prev'),
					shortcut: $sc.find('prev', 'install'),
					isPrimary: true,
					icon: 'arrow-right',
					handle: this.onPrevClicked,
				},
				validateButtonOptions: {
					title: this.$t('install.buttons.checkAndContinue'),
					isPrimary: true,
				},
				installButtonOptions: {
					title: this.$t('install.buttons.install'),
					shortcut: $sc.find('done', 'install'),
					isPrimary: true,
					icon: 'cog',
					handle: this.onInstallClicked,
				},

				validators: {
					environment: {
						adminDetails: {
							username: validators('required'),
							password: validators('required'),
							confirmPassword: validators('required'),
						},
						company: {
							name: validators('required'),
						},
						fiscalPeriod: {
							name: validators('required'),
							startDate: validators('required'),
							endDate: validators('required'),
						},
					},
				},

				lockControls: false,
				isSending: false,
			}
		},
		computed: {
			...mapState({
				dialogMessages: (state) => state.messages,
			}),
			...mapGetters({
				currentUser: 'useraccount/currentUser',
				currentUserDetails: 'useraccount/currentUserDetails'
			}),
			currentStepIndex() {
				if (!this.steps || !this.currentStep) {
					return 0
				}
				return this.steps.indexOf(this.currentStep)
			},
			currentStepId() {
				return this.stepList[this.currentStepIndex]?.id || 'admin-details'
			},
			isFirstStep() {
				return this.currentStepIndex === 0
			},
			isLastStep() {
				return this.currentStepIndex === this.steps.length - 1
			},
			completedSteps() {
				return this.stepList.slice(0, this.currentStepIndex)
			},
			futureSteps() {
				return this.stepList.slice(this.currentStepIndex + 1)
			},
			hasRemainingSteps() {
				return this.futureSteps.length > 0
			},
			stepTitle() {
				return this.currentStepId
			},
		},
		created() {
			// if (this.currentUser) {
			// 	this.useraccount_clearAll()
			// 	window.location.reload()
			// }
		},
		mounted() {
			this.start()
			document.addEventListener('click', (event) => {
				let $target = $(event.target)
				if ($target.parents('.install-container').length === 0) {
					this.focusStartup()
				}
			})
		},
		methods: {
			...mapActions({
				useraccount_clearAll: 'useraccount/clearAll'
			}),
			start() {
				this.onLockStepStateChanged(true)
				if (this.checkCustomerCode())
					this.getInstallStatus()
			},
			checkCustomerCode() {
				if (!this.currentUserDetails || !this.currentUserDetails.customerCode) {
					router.push({ name: 'launcher' })
					return false
				}
				return true
			},
			environmentChanged(val) {
				this.environment = val
			},
			onNextClicked(button, event) {
				if (event && event.preventDefault) {
					event.preventDefault()
				}
				let index = this.getNextStepIndex()
				if (this.validateInputs()) {
					if (index < this.steps.length) {
						this.direction = 'forward'
						this.currentStep = this.steps[index]
						this.refresh()
					}
				}
			},
			onPrevClicked(button, event) {
				if (event && event.preventDefault) {
					event.preventDefault()
				}
				let index = this.getPrevStepIndex()
				if (index >= 0) {
					this.direction = 'backward'
					this.currentStep = this.steps[index]
					this.refresh()
				} else {
					router.push({ name: 'launcher' })
				}
			},
			onInstallClicked(button, event) {
				if (event && event.preventDefault) {
					event.preventDefault()
				}
				this.submitForm()
			},
			onButtonShortcutClicked(button, event) {
				if (event && event.preventDefault) {
					event.preventDefault()
				}
				let handle = button.handle
				if (handle != null) {
					handle(button, event)
				}
			},
			onLockStepStateChanged(state) {
				this.lockControls = state === true
			},
			onStepEntered() {
				this.refresh()
			},
			focusStartup() {
				this.defer(() => {
					let el = $(this.$refs['component-container'])
					if (el.length > 0) {
						let startupEl = el.find('[startup]:visible:not([disabled])')
						if (startupEl.length > 0) {
							startupEl[0].focus()
						} else {
							let firstInput = el.find('[tabindex]:visible:not([disabled])').first()
							if (firstInput.length > 0) {
								firstInput[0].focus()
							} else if (this.$refs.wizard) {
								this.$refs.wizard.focus()
							}
						}
					}
				}, 40)
			},
			refresh() {
				this.focusStartup()
				this.defer(this.subscribeKeys, 60)
			},

			getNextStepIndex() {
				if (this.currentStep == null) {
					return 0
				}
				return this.steps.indexOf(this.currentStep) + 1
			},
			getPrevStepIndex() {
				if (this.currentStep == null) {
					return 0
				}
				return this.steps.indexOf(this.currentStep) - 1
			},
			submitForm() {
				this.isSending = true

				this.environment.adminDetails.customerCode = this.currentUserDetails.customerCode

				return new Promise((resolve, reject) => {
					post('/api/v1.0/system/install/install', this.environment, null, { ignoreCommercialAccess: true })
						.then((result) => {
							this.isSending = false
							if (result.success) {
								this.$router.push({ name: 'login' })
							}
							resolve(result)
						})
						.catch((error) => {
							this.isSending = false
							reject(error.message)
						})
				})
			},

			getInstallStatus() {
				return new Promise((resolve, reject) => {
					post('/api/v1.0/system/install/getStatus', null, null, { ignoreCommercialAccess: true })
						.then((result) => {
							if (result.data.coreIsInitialized) {
								this.$router.push({ name: 'login' })
								resolve(result.data)
							} else {
								this.onLockStepStateChanged(false)
								this.currentStep = this.steps[0]
								this.refresh()
							}
						})
						.catch((error) => {
							reject(error.message)
						})
				})
			},

			validateInputs() {
				let checker = new Checker((field) =>
					this.$t(`install.adminDetails.f.${field}`)
				)
				let errors = []

				if (this.environment.company && this.stepTitle === 'company-fiscal') {
					checker = new Checker((name) => this.$t(`install.company.f.${name}`))
					errors.push(
						...checker.test(
							this.environment.company,
							this.validators.environment.company
						)
					)
				}

				if (this.environment.adminDetails && this.stepTitle === 'admin-details') {
					checker = new Checker((name) => this.$t(`install.adminDetails.f.${name}`))
					errors.push(
						...checker.test(
							this.environment.adminDetails,
							this.validators.environment.adminDetails
						)
					)
				}

				if (this.environment.fiscalPeriod && this.stepTitle === 'company-fiscal') {
					checker = new Checker((name) =>
						this.$t(`install.fiscalPeriod.f.${name}`)
					)
					errors.push(
						...checker.test(
							this.environment.fiscalPeriod,
							this.validators.environment.fiscalPeriod
						)
					)
				}

				this.validationToast(errors)
				return errors.length === 0
			},
			subscribeKeys() {
				this.$keyHandler.ReserveAt('install-taborder-ctrl-exit-tabindex', 0)
				this.$keyHandler.ReserveAt('install-taborder-ctrl-enter-tabscope-up', 0)
				this.$keyHandler.ReserveAt('install-taborder-ctrl-enter-tabscope-down', 0)
				this.$keyHandler.ReserveAt('install-taborder-ctrl-enter-tabscope-shift', 0)
				this.$keyHandler.ReserveAt('install-taborder-ctrl-enter-tabscope', 0)
				this.$keyHandler.ReserveAt('install-taborder-previous-shift', 0)
				this.$keyHandler.ReserveAt('install-taborder-previous', 0)
				this.$keyHandler.ReserveAt('install-taborder-next', 0)

				this.$keyHandler.Subscribe({
					name: 'install-button-click',
					el: this.$el,
					ctrlKey: undefined,
					altKey: undefined,
					shiftKey: undefined,
					context: this,
					handler: function (sender, event) {
						let button = _.head(
							[
								this.nextButtonOptions,
								this.prevButtonOptions,
								this.installButtonOptions,
							].filter((b) => {
								return (
									b.shortcut.keyCode === event.keyCode &&
									(b.shortcut.ctrlKey == null ||
										b.shortcut.ctrlKey === event.ctrlKey) &&
									(b.shortcut.shiftKey == null ||
										b.shortcut.shiftKey === event.shiftKey) &&
									(b.shortcut.altKey == null ||
										b.shortcut.altKey === event.altKey)
								)
							})
						)
						if (button) {
							this.onButtonShortcutClicked(button, event)
							return true
						}
					},
				})

				TabOrder.subscribeKeys('install', this)
			},
		},
	}
</script>

<style lang="less" src="~/styles/install.less"></style>
